function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function formatConfigLabel(value) {
  return String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function parseKeyValueString(value) {
  if (typeof value !== "string") return null;

  const entries = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const separatorIndex = part.indexOf(":");
      if (separatorIndex === -1) return null;

      return [
        part.slice(0, separatorIndex).trim(),
        part.slice(separatorIndex + 1).trim(),
      ];
    })
    .filter(Boolean);

  if (entries.length < 2) return null;

  return Object.fromEntries(entries);
}

export function normalizePreprocessingSteps(value) {
  if (value === undefined || value === null || value === "") return [];

  if (Array.isArray(value)) {
    return value
      .map((step, index) => {
        if (isPlainObject(step)) {
          const entries = Object.entries(step);
          if (entries.length === 1) {
            const [name, params] = entries[0];
            return { name: formatConfigLabel(name), params };
          }
          return { name: `Step ${index + 1}`, params: step };
        }

        return { name: `Step ${index + 1}`, params: step };
      })
      .filter((step) => step.name || step.params);
  }

  if (typeof value === "string") {
    const stepMatch = value.match(/^([^:]+):\s*(.+)$/);
    if (stepMatch) {
      const [, stepName, remainder] = stepMatch;
      const parsedParams = parseKeyValueString(remainder);
      return [
        {
          name: formatConfigLabel(stepName),
          params: parsedParams || remainder.trim(),
        },
      ];
    }

    const parsed = parseKeyValueString(value);
    if (parsed) {
      return [{ name: "Preprocessing", params: parsed }];
    }

    return [{ name: value.trim(), params: null }];
  }

  if (isPlainObject(value)) {
    return Object.entries(value)
      .filter(([, params]) => params !== undefined && params !== null && params !== "")
      .map(([name, params]) => ({
        name: formatConfigLabel(name),
        params,
      }));
  }

  return [{ name: String(value), params: null }];
}

function formatPreprocessingParamValue(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([key, item]) => `${formatConfigLabel(key)}: ${formatPreprocessingParamValue(item)}`)
      .join(", ");
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatPreprocessingParamValue(item)).join(", ");
  }
  return String(value);
}

export function formatPreprocessingText(value) {
  const steps = normalizePreprocessingSteps(value);
  if (!steps.length) return "-";

  return steps
    .map((step) => {
      if (isPlainObject(step.params)) {
        const params = Object.entries(step.params)
          .map(([key, item]) => `${formatConfigLabel(key)}: ${formatPreprocessingParamValue(item)}`)
          .join(", ");
        return `${step.name} (${params})`;
      }

      if (step.params !== null && step.params !== undefined && step.params !== "") {
        return `${step.name}: ${formatPreprocessingParamValue(step.params)}`;
      }

      return step.name;
    })
    .join(" → ");
}

export function formatListText(value) {
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item ?? "").trim()).filter(Boolean);
    return items.length ? items.join(", ") : "-";
  }

  if (typeof value === "string") {
    const items = value
      .split(/[,;|]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    return items.length ? items.join(", ") : value || "-";
  }

  return value === undefined || value === null || value === "" ? "-" : String(value);
}

export function formatScalarText(value) {
  if (value === undefined || value === null || value === "") return "-";
  if (Array.isArray(value) || isPlainObject(value)) return formatListText(value);
  return String(value);
}
